import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../App";
import { usePermissions } from "../../context/PermissionsContext";
import CRMContextBanner from "../../components/CRMContextBanner";
import CRMSidebar from "../../components/CRMSidebar";
import API from "../../utils/api";
import PipelineFunnel from "../../components/PipelineFunnel";
import AnalyticsDashboard from "../../components/AnalyticsDashboard";
import ReportsExport from "../../components/ReportsExport";
import SalesLearning from "../../components/SalesLearning";
import TargetCenter from "../../components/TargetCenter";
import BulkUpload from "../../components/BulkUpload";
import AutomationRules from "./AutomationRules";
import WhatsAppCenter from "./WhatsAppCenter";
import EmailCenter from "./EmailCenter";
import TopDeals from "../../components/TopDeals";
import FieldDayMap from "../../components/FieldDayMap";
import AuditTrail from "../../components/AuditTrail";
import DailyProductivity from "../DailyProductivity";
import MessageTemplates from "./MessageTemplates";
import LeadActionCenter from "../../components/LeadActionCenter";
import FollowUpSlaDashboard from "../../components/FollowUpSlaDashboard";
import LeadDataQuality from "../../components/LeadDataQuality";
import InstitutionAccounts from "../../components/InstitutionAccounts";
import ExistingCustomerImport from "../../components/ExistingCustomerImport";
import RevenueForecast from "../../components/RevenueForecast";
import FieldGrowthCenter from "../../components/FieldGrowthCenter";
import RouteReview from "../../components/RouteReview";
import FieldBriefing from "../../components/FieldBriefing";
import CommandCenter from "../../components/CommandCenter";

const HRMS_URL      = process.env.REACT_APP_HRMS_URL      || "http://localhost:5000";
const EXPENSE_URL     = process.env.REACT_APP_EXPENSE_URL     || "http://localhost:4000";
const EXPENSE_API_URL = process.env.REACT_APP_EXPENSE_API_URL || "http://localhost:4000/api";

// "admin" role removed from the system — all users are employee, manager, or superadmin
const ROLES = ["employee", "manager"];
const COMPANIES = ["ebek", "ebekmed"];

const ROLE_CFG = {
  employee: { label: "Employee", color: "#6366f1", bg: "#eef2ff" },
  manager:  { label: "Manager",  color: "#0891b2", bg: "#ecfeff" },
};

const EMPTY_FORM = {
  name: "", email: "", phone: "", password: "", role: "employee", company: "ebek",
  manager_id: "", department: "",
  home_address: "", home_pincode: "", home_city: "", home_latitude: "", home_longitude: ""
};

// Maps CRM admin nav keys → permission module keys (null = always show)
// Keys MUST match what AccessControl.jsx stores in role_permissions.module_key
const ADMIN_NAV_MODULE_MAP = {
  overview:     null,
  field:        "my_visits",          // was "field_day"
  employees:    null,                 // not in DB — admin-inherent, always show
  pipeline:     null,                 // not in DB — admin-inherent, always show
  followups:    "follow_ups",         // was "followups"
  targets:      null,                 // not in DB — admin-inherent, always show
  "top-deals":  null,                 // not in DB — admin-inherent, always show
  automation:   "automation",
  learning:     "learning",
  flagged:      null,                 // not in DB — admin-inherent, always show
  analytics:    null,                 // not in DB — admin-inherent, always show
  trail:        "audit_log",
  reports:      "reports",
  routes:       null,                 // superadmin-only — gated where the nav is built
  upload:       null,                 // bulk lead import — admin-inherent, always show
  whatsapp:     "whatsapp_center",    // was "whatsapp"
  email:        "email_center",       // was "email"
  productivity: null,
  templates:    null,                 // message templates — admin-inherent
  actions:      null,
  quality:      null,
  accounts:     null,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const accent = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  // Restricted admin = CRM admin but NOT superadmin (functionally a manager/TL)
  const crmRole = user?.app_role_overrides?.crm || user?.role;
  const isRestrictedAdmin = crmRole === "admin" && user?.role !== "superadmin";

  const [tab, setTab] = useState("overview");

  const ALL_ADMIN_NAV = [
    ["overview", "⌂", "Home"],
    ["field",    "📍", "Field Day"],
    ["briefing", "📊", "Field Briefing"],
    ...(user?.role === "superadmin" ? [["routes", "🛰", "Route Review"]] : []),
    ["employees","👥", "Employees"],
    ["followups","⏰", "Follow-ups"],
    ["pipeline", "💼", "Pipeline"],
    ["actions",  "✅", "Lead Action Requests"],
    ["productivity","📈","Productivity"],
    ["targets",  "🎯", "Targets"],
    ["top-deals","✨", "Top Deals"],
    ["flagged",  "🚩", "Flagged"],
    ["accounts", "AC", "Institutions"],
    ["customers","CU", "Existing Clients"],
    ["quality",  "DQ", "Data Quality"],
    ["growth",   "FG", "Field Growth"],
    ["forecast", "FC", "Forecast"],
    ["analytics","📊", "Analytics"],
    ["trail",    "🔍", "Audit Trail"],
    ["reports",  "📋", "Reports"],
    ["learning", "📚", "Learning"],
    ["automation","⚡","Automation"],
    ...(!isRestrictedAdmin ? [["upload","📥","Bulk Upload"]] : []),
    ["whatsapp", "💬", "WhatsApp"],
    ["email",    "✉️", "Email"],
    ["templates","📝", "Templates"],
  ];

  // Filter nav based on module permissions (superadmin always sees all)
  const ADMIN_NAV = ALL_ADMIN_NAV.filter(([k]) => {
    if (["whatsapp", "email"].includes(k)) return false;
    const modKey = ADMIN_NAV_MODULE_MAP[k];
    if (!modKey) return true;
    return can(modKey);
  });
  const [employees, setEmployees] = useState([]);
  const [followupStats, setFollowupStats] = useState([]);
  const [loading, setLoading] = useState(true);

  // User management
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [searchQ, setSearchQ] = useState("");

  // Flagged Actions
  const [flaggedActions, setFlaggedActions]         = useState([]);

  // Field Day tracking
  const [fieldDate, setFieldDate]     = useState(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }));
  const [fieldCheckins, setFieldCheckins] = useState([]);
  const [fieldLoading, setFieldLoading]   = useState(false);
  const [fieldMapUser, setFieldMapUser]   = useState(null);


  const fetchFollowupStats = useCallback(async () => {
    try {
      const res = await API.get("/followups/stats");
      setFollowupStats(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await API.get("/users");
      setEmployees(res.data);
    } catch { toast.error("Failed to load employees"); }
  }, []);


  const fetchFlaggedActions = useCallback(async () => {
    try {
      const res = await API.get("/follow-up-actions/flagged");
      setFlaggedActions(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchEmployees(), fetchFlaggedActions(), fetchFollowupStats()]);
    } finally {
      setLoading(false);
    }
  }, [fetchEmployees, fetchFlaggedActions, fetchFollowupStats]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Add user removed — employees are added only via HRMS Admin
  // GPS for employee home location
  const [gettingHomeGPS, setGettingHomeGPS] = useState(false);
  const captureHomeGPS = () => {
    setGettingHomeGPS(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(f => ({ ...f, home_latitude: pos.coords.latitude.toFixed(7), home_longitude: pos.coords.longitude.toFixed(7) }));
        setGettingHomeGPS(false);
      },
      () => { toast.error("Could not get GPS location"); setGettingHomeGPS(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const openEdit = (emp) => {
    setForm({
      name: emp.name, email: emp.email, phone: emp.phone || "", password: "", role: emp.role, company: emp.company,
      manager_id: emp.manager_id || "",
      department: emp.department || "",
      home_address: emp.home_address || "", home_pincode: emp.home_pincode || "",
      home_city: emp.home_city || "",
      home_latitude: emp.home_latitude || "", home_longitude: emp.home_longitude || ""
    });
    setEditUser(emp);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email required");
    if (!editUser) return; // add user disabled — use HRMS Admin
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone || null, role: form.role, company: form.company,
        manager_id: form.manager_id || null,
        department: form.department || null,
        home_address: form.home_address || null,
        home_pincode: form.home_pincode || null,
        home_city: form.home_city || null,
        home_latitude: form.home_latitude || null,
        home_longitude: form.home_longitude || null,
      };
      if (form.password) payload.password = form.password;
      await API.put(`/users/${editUser.id}`, payload);
      toast.success("User updated");
      setShowForm(false);
      fetchEmployees();
    } catch (err) { toast.error(err.response?.data?.error || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleResetPwd = async (emp) => {
    const pwd = prompt(`New password for ${emp.name}:`);
    if (!pwd) return;
    try {
      await API.put(`/users/${emp.id}/reset-password`, { new_password: pwd });
      toast.success("Password reset");
    } catch { toast.error("Reset failed"); }
  };

  const filtered = employees.filter(e =>
    e.name.toLowerCase().includes(searchQ.toLowerCase()) ||
    e.email.toLowerCase().includes(searchQ.toLowerCase())
  );

  const inp = { width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none" };
  const lbl = { fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 6, display: "block" };

  return (
    <div className="sales-admin-shell sales-admin-role-shell" style={{ minHeight: "100vh", display: "flex", fontFamily: "'Inter',sans-serif" }}>
      {/* ── Vertical Sidebar ── */}
      <CRMSidebar
        user={user}
        tab={tab}
        setTab={(k) => {
          setTab(k);
          if (k === "field") {
            setFieldLoading(true);
            const d = fieldDate;
            API.get(`/checkins/report?from=${d}&to=${d}`)
              .then(r => setFieldCheckins((r.data || []).filter(c => c.plan_type === "institution_visit")))
              .catch(() => {})
              .finally(() => setFieldLoading(false));
          }
        }}
        navItems={ADMIN_NAV}
        flaggedCount={0}
        logout={logout}
      />

      {/* ── Main content ── */}
      <div style={{ flex: 1, minWidth: 0, overflowY: "auto", background: "#f8fafc" }}>
        {/* Top bar */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56, position: "sticky", top: 0, zIndex: 30 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b" }}>
              {ADMIN_NAV.find(n => n[0] === tab)?.[2] || "Home"}
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>{user?.name} · {user?.company?.toUpperCase()} Admin</div>
          </div>
        </div>

      <div className="sales-admin-content" style={{ padding: "20px", maxWidth: tab === "overview" ? 1100 : 860, margin: "0 auto" }}>
        {loading && tab !== "overview" && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <div style={{ display: "inline-block", width: 36, height: 36, border: `3px solid ${accent}33`, borderTopColor: accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <div style={{ marginTop: 14, fontSize: 13, color: "#64748b", fontWeight: 600 }}>Loading dashboard…</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {tab === "overview" && (
          <CommandCenter
            accent={accent}
            onOpenTab={(k) => {
              const mapped = k === "leads" ? "pipeline" : k;
              setTab(mapped);
              if (mapped === "field") {
                setFieldLoading(true);
                const d = fieldDate;
                API.get(`/checkins/report?from=${d}&to=${d}`)
                  .then(r => setFieldCheckins((r.data || []).filter(c => c.plan_type === "institution_visit")))
                  .catch(() => {})
                  .finally(() => setFieldLoading(false));
              }
            }}
            onOpenPerson={(person, day) => setFieldMapUser({ id: person.user_id, name: person.name, date: day })}
            drillItems={[
              ["field", "Visits & maps"],
              ["pipeline", "Pipeline"],
              ["followups", "Follow-ups"],
              ["briefing", "Evening briefing"],
              ["reports", "Exports"],
            ]}
          />
        )}

        {/* FIELD DAY TAB */}
        {tab === "field" && (
          <>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"12px 16px" }}>
              <span style={{ fontSize:13, fontWeight:700, color:"#475569" }}>Date:</span>
              <input
                type="date"
                value={fieldDate}
                onChange={e => {
                  const d = e.target.value;
                  setFieldDate(d);
                  setFieldLoading(true);
                  API.get(`/checkins/report?from=${d}&to=${d}`)
                    .then(r => setFieldCheckins((r.data || []).filter(c => c.plan_type === "institution_visit")))
                    .catch(() => {})
                    .finally(() => setFieldLoading(false));
                }}
                style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 10px", fontSize:13, fontFamily:"inherit" }}
              />
              <span style={{ fontSize:12, color:"#94a3b8", marginLeft:4 }}>
                {fieldCheckins.length} rep{fieldCheckins.length !== 1 ? "s" : ""} on field visit
              </span>
            </div>

            {fieldLoading && <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>Loading...</div>}

            {!fieldLoading && fieldCheckins.length === 0 && (
              <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:60, textAlign:"center" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
                <div style={{ fontWeight:700, color:"#1e293b", marginBottom:4 }}>No field visits on this day</div>
                <div style={{ fontSize:13, color:"#94a3b8" }}>Reps checked in as "Institution Visit" will appear here</div>
              </div>
            )}

            {!fieldLoading && fieldCheckins.map(c => (
              <div
                key={c.user_id || c.id}
                style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", alignItems:"center", gap:14, cursor:"pointer" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                onClick={() => setFieldMapUser({ id: c.user_id, name: c.name, date: fieldDate })}
              >
                <div style={{ width:44, height:44, borderRadius:12, background:accent+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:900, color:accent, flexShrink:0 }}>
                  {(c.name || "?")[0]}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#1e293b" }}>{c.name}</div>
                  <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>
                    {c.email}
                    {c.check_in_time ? ` · In ${new Date(c.check_in_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}` : ""}
                    {c.check_out_time ? ` · Out ${new Date(c.check_out_time).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}` : " · Active"}
                    {c.total_km_travelled ? ` · ${Number(c.total_km_travelled).toFixed(1)} km` : ""}
                  </div>
                </div>
                <div style={{ background:accent+"15", color:accent, borderRadius:20, padding:"6px 14px", fontSize:12, fontWeight:700, flexShrink:0 }}>
                  📍 View Map →
                </div>
              </div>
            ))}
          </>
        )}

        {/* EMPLOYEES TAB */}
        {!loading && tab === "employees" && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder=" Search name or email?"
                style={{ flex: 1, padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              <div style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", padding: "0 4px" }}>
                Add employees via HRMS Admin
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map(emp => {
                const rc = ROLE_CFG[emp.role] || ROLE_CFG.employee;
                return (
                  <div key={emp.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: accent + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: accent, flexShrink: 0 }}>
                      {emp.name[0]?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{emp.name}</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>{emp.email} {emp.phone ? ` - ${emp.phone}` : ""}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                        <span style={{ background: rc.bg, color: rc.color, borderRadius: 100, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{rc.label}</span>
                        <span style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 100, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{emp.company?.toUpperCase()}</span>
                        {emp.department && <span style={{ background: "#eff6ff", color: "#1d4ed8", borderRadius: 100, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{emp.department}</span>}
                        {emp.home_city ? (
                          <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 100, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                             {emp.home_city}{emp.home_pincode ? ` - ${emp.home_pincode}` : ""}
                          </span>
                        ) : (
                          <span style={{ background: "#fff7ed", color: "#92400e", borderRadius: 100, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                             No home location
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => openEdit(emp)} style={{ background: "#f1f5f9", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#475569" }}>Edit</button>
                      <button onClick={() => handleResetPwd(emp)} style={{ background: "#fef9c3", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#92400e" }}></button>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>No employees found</div>}
            </div>
          </>
        )}


        {/* AUDIT TRAIL TAB — semantic business event log */}
        {tab === "trail" && (
          <AuditTrail accent={accent} />
        )}

        {/* ANALYTICS TAB */}
        {tab === "analytics" && (
          <AnalyticsDashboard accent={accent} userRole={user?.role} userCompany={user?.company} />
        )}

        {/* REPORTS TAB */}
        {tab === "reports" && (
          <ReportsExport accent={accent} userRole={user?.role} userCompany={user?.company} />
        )}

        {/* UPLOAD TAB */}
        {tab === "upload" && (
          <BulkUpload accent={accent} />
        )}
        {tab === "top-deals" && <TopDeals accent={accent} limit={20} />}

        {tab === "automation" && (
          <AutomationRules accent={accent} />
        )}

        {tab === "targets" && (
          <TargetCenter accent={accent} managementView />
        )}

        {/* PIPELINE TAB */}
        {tab === "pipeline" && <PipelineFunnel accent={accent} />}
        {tab === "actions" && <LeadActionCenter accent={accent} />}
        {tab === "quality" && <LeadDataQuality accent={accent} />}
        {tab === "accounts" && <InstitutionAccounts accent={accent} />}
        {tab === "customers" && <ExistingCustomerImport accent={accent} />}
        {tab === "forecast" && <RevenueForecast accent={accent} />}
        {tab === "growth" && <FieldGrowthCenter accent={accent} />}
        {tab === "briefing" && <FieldBriefing accent={accent} />}
        {tab === "routes" && user?.role === "superadmin" && <RouteReview accent={accent} />}

        {/* FOLLOW-UPS TAB */}
        {!loading && tab === "followups" && (
          <>
            <FollowUpSlaDashboard accent={accent} />
            {/* Summary row */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
              {[
                { label:"Total",   value: followupStats.reduce((a,r)=>a+parseInt(r.total_followups||0),0), color:"#6366f1", icon:"" },
                { label:"Done",    value: followupStats.reduce((a,r)=>a+parseInt(r.completed||0),0),        color:"#16a34a", icon:"" },
                { label:"Missed",  value: followupStats.reduce((a,r)=>a+parseInt(r.missed||0),0),           color:"#dc2626", icon:"" },
                { label:"Overdue", value: followupStats.reduce((a,r)=>a+parseInt(r.overdue||0),0),          color:"#ea580c", icon:"" },
              ].map(s => (
                <div key={s.label} style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:"14px 8px", textAlign:"center" }}>
                  <div style={{ fontSize:18, marginBottom:3 }}>{s.icon}</div>
                  <div style={{ fontSize:24, fontWeight:900, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:10, color:"#94a3b8", fontWeight:600 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Per-employee table */}
            <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, overflow:"hidden" }}>
              <div style={{ padding:"14px 20px", borderBottom:"1px solid #f1f5f9", fontSize:14, fontWeight:700 }}> Follow-up Performance - Last 30 Days</div>
              {/* Table header */}
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr", padding:"10px 20px", background:"#f8fafc", fontSize:11, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:0.5 }}>
                <span>Employee</span><span style={{textAlign:"center"}}>Total</span><span style={{textAlign:"center"}}>Done</span><span style={{textAlign:"center"}}>Missed</span><span style={{textAlign:"center"}}>Overdue</span><span style={{textAlign:"center"}}>Rate</span>
              </div>
              {followupStats.length === 0 ? (
                <div style={{ padding:40, textAlign:"center", color:"#94a3b8", fontSize:13 }}>No follow-up data yet</div>
              ) : followupStats.map(r => {
                const total = parseInt(r.total_followups || 0);
                const done  = parseInt(r.completed || 0);
                const miss  = parseInt(r.missed    || 0);
                const over  = parseInt(r.overdue   || 0);
                const pct   = total > 0 ? Math.round(done / total * 100) : 0;
                const coAccent = r.rep_company === "ebekmed" ? "#2AB5A5" : "#E31E24";
                return (
                  <div key={r.user_id} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr", padding:"12px 20px", borderBottom:"1px solid #f8fafc", alignItems:"center" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:"#1e293b" }}>{r.rep_name}</div>
                      <div style={{ fontSize:11, color: coAccent, fontWeight:600 }}>{r.rep_company === "ebekmed" ? "Ebek Med" : "Ebek"}</div>
                    </div>
                    <div style={{ textAlign:"center", fontSize:16, fontWeight:800, color:"#6366f1" }}>{total}</div>
                    <div style={{ textAlign:"center", fontSize:16, fontWeight:800, color:"#16a34a" }}>{done}</div>
                    <div style={{ textAlign:"center", fontSize:16, fontWeight:800, color: miss>0?"#dc2626":"#94a3b8" }}>{miss}</div>
                    <div style={{ textAlign:"center", fontSize:16, fontWeight:800, color: over>0?"#ea580c":"#94a3b8" }}>{over}</div>
                    <div style={{ textAlign:"center" }}>
                      <span style={{ padding:"3px 8px", borderRadius:20, fontSize:11, fontWeight:700,
                        background: pct>=70?"#f0fdf4": pct>=40?"#fff7ed":"#fef2f2",
                        color:      pct>=70?"#16a34a": pct>=40?"#ea580c":"#dc2626" }}>
                        {pct}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {/* -- FLAGGED ACTIONS TAB -- */}
        {tab === "flagged" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}> Flagged Location Mismatches</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                Employees who logged a Direct Meeting but were &gt;500m from the institution - visible to managers/admins only
              </div>
            </div>

            {flaggedActions.length === 0 ? (
              <div style={{ textAlign: "center", padding: 60, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}></div>
                <div style={{ fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>No flagged actions</div>
                <div style={{ fontSize: 13, color: "#94a3b8" }}>All direct meets have been within the expected location range</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {flaggedActions.map(a => (
                  <div key={a.id} style={{ background: "#fff", border: "1.5px solid #fecaca", borderRadius: 14, padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b" }}>{a.institution_name}</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                           {a.employee_name} - {a.employee_email}
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                          {new Date(a.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                        <div style={{ padding: "4px 12px", background: "#fef2f2", borderRadius: 20, color: "#dc2626", fontWeight: 800, fontSize: 13 }}>
                           {a.location_mismatch_meters}m away
                        </div>
                      </div>
                    </div>
                    {a.manager_flag_note && (
                      <div style={{ background: "#fef2f2", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#9a3412", lineHeight: 1.5 }}>
                         {a.manager_flag_note}
                      </div>
                    )}
                    {a.notes && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "#64748b", background: "#f8fafc", borderRadius: 8, padding: "6px 10px" }}>
                         {a.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* -- LEARNING TAB -- */}
        {tab === "learning" && (
          <SalesLearning accent={accent} />
        )}

        {tab === "whatsapp" && <WhatsAppCenter user={user} />}
        {tab === "email" && <EmailCenter user={user} />}
        {tab === "templates" && <MessageTemplates user={user} />}
        {tab === "productivity" && <DailyProductivity />}

      </div>

      {/* Add/Edit User Modal */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100, display: "flex", alignItems: "flex-end" }}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Edit User</div>
              <button onClick={() => setShowForm(false)} style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}></button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
              {/* Basic Info */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>FULL NAME *</label>
                  <input value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Employee name" style={inp} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>EMAIL *</label>
                  <input type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="email@company.in" style={inp} />
                </div>
                <div>
                  <label style={lbl}>PHONE</label>
                  <input type="tel" value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="+91 XXXXX" style={inp} />
                </div>
                <div>
                  <label style={lbl}>{editUser ? "NEW PASSWORD (leave blank)" : "PASSWORD *"}</label>
                  <input type="password" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} placeholder="********" style={inp} />
                </div>
                <div>
                  <label style={lbl}>ROLE</label>
                  <select value={form.role} onChange={e => setForm(f=>({...f,role:e.target.value}))} style={{ ...inp, cursor: "pointer" }}>
                    {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>COMPANY</label>
                  <select value={form.company} onChange={e => setForm(f=>({...f,company:e.target.value}))} style={{ ...inp, cursor: "pointer" }}>
                    {COMPANIES.map(c => <option key={c} value={c}>{c === "ebekmed" ? "Ebek Med" : "Ebek"}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>DEPARTMENT</label>
                  <input value={form.department} onChange={e => setForm(f=>({...f,department:e.target.value}))} placeholder="e.g. Sales, Operations, HR?" style={inp} />
                </div>
              </div>

              {/* Home / Registered Location */}
              <div style={{ background: "#f0fdf4", borderRadius: 14, padding: 14, border: "1.5px solid #86efac" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#15803d", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                   Home / Registered Location
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12 }}>
                  Used to detect city mismatch when employees work from home. If they log in from a different city, it gets flagged silently for managers.
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <label style={lbl}>HOME ADDRESS</label>
                    <input value={form.home_address} onChange={e => setForm(f=>({...f,home_address:e.target.value}))} placeholder="Street, Area, City" style={inp} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={lbl}>CITY</label>
                      <input value={form.home_city} onChange={e => setForm(f=>({...f,home_city:e.target.value}))} placeholder="e.g. Chennai" style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>PINCODE</label>
                      <input type="tel" maxLength={6} value={form.home_pincode} onChange={e => setForm(f=>({...f,home_pincode:e.target.value}))} placeholder="600001" style={inp} />
                    </div>
                  </div>

                  {/* GPS Coordinates */}
                  <div style={{ background: "#fff", borderRadius: 10, padding: 12, border: "1px solid #d1fae5" }}>
                    <label style={{ ...lbl, color: "#15803d" }}>GPS COORDINATES</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                      <div>
                        <label style={{ fontSize: 10, color: "#94a3b8", marginBottom: 3, display: "block" }}>LATITUDE</label>
                        <input type="number" step="any" value={form.home_latitude}
                          onChange={e => setForm(f=>({...f,home_latitude:e.target.value}))}
                          placeholder="e.g. 13.0827" style={{ ...inp, fontSize: 12 }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: "#94a3b8", marginBottom: 3, display: "block" }}>LONGITUDE</label>
                        <input type="number" step="any" value={form.home_longitude}
                          onChange={e => setForm(f=>({...f,home_longitude:e.target.value}))}
                          placeholder="e.g. 80.2707" style={{ ...inp, fontSize: 12 }} />
                      </div>
                    </div>
                    <button onClick={captureHomeGPS} disabled={gettingHomeGPS}
                      style={{ width: "100%", padding: "9px", background: "#15803d", color: "#fff", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                      {gettingHomeGPS ? " Getting location..." : " Use Current Location as Home"}
                    </button>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 5, textAlign: "center" }}>
                      Or enter coordinates manually from Google Maps (right-click, copy coordinates)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button onClick={handleSave} disabled={saving}
              style={{ width: "100%", padding: "14px", background: accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {saving ? "Saving..." : editUser ? "Save Changes" : "Create User"}
            </button>
          </div>
        </div>
      )}
      {fieldMapUser && (
        <FieldDayMap
          userId={fieldMapUser.id}
          userName={fieldMapUser.name}
          date={fieldMapUser.date || fieldDate}
          accent={accent}
          onClose={() => setFieldMapUser(null)}
        />
      )}
      </div>  {/* main content */}
    </div>
  );
}
